import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { OverlayTrigger, Table, Tooltip } from 'react-bootstrap';

import Index from '~/pages/Index';
import Posts from '~/components/Posts';
import avatar from '~/assets/img/acordeonAvatar.svg';
import { ReactComponent as UndrawOrganizeResume } from '~/assets/img/undraw_Organize_resume.svg';

import * as S from './styles';
import { useProfile } from '~/hooks/profile';
import { isProfileComplete } from '~/utils/profileUtils';
import Loading from '~/components/Loading';
import {
  useFindRankingQuery,
  useFindRankingByPeriodLazyQuery,
  useFindMyRankingByPeriodLazyQuery,
} from '~/graphql/generated/graphql';

const RANKING_START_YEAR = 2025;
const RANKING_START_MONTH = 1;

function generateMonthOptions() {
  const options = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
  ];

  let year = RANKING_START_YEAR;
  let month = RANKING_START_MONTH;

  while (year < currentYear || (year === currentYear && month <= currentMonth)) {
    options.push({
      label: `${monthNames[month - 1]} / ${year}`,
      value: `${year}-${String(month).padStart(2, '0')}`,
    });
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }

  return options.reverse();
}

function getMonthPeriod(yearMonth) {
  const [year, month] = yearMonth.split('-').map(Number);
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0, 23, 59, 59);

  const fmt = d =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  return { startDate: fmt(start), endDate: fmt(end) };
}

function getCurrentYearMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export default function RankingGeneral() {
  const { dataPerson, dataProfile, profileComplete } = useProfile();
  const [activeTab, setActiveTab] = useState('mes');
  const [selectedMonth, setSelectedMonth] = useState(getCurrentYearMonth);

  const monthOptions = useMemo(() => generateMonthOptions(), []);

  const { data: dataGeral, loading: loadingGeral } = useFindRankingQuery();
  const listUsersRanking = dataGeral?.findRanking ?? null;

  const [fetchPeriod, { data: dataPeriod, loading: loadingPeriod }] =
    useFindRankingByPeriodLazyQuery();
  const listUsersPeriod = dataPeriod?.findRankingByPeriod ?? null;

  const [fetchMyPeriod, { data: dataMyPeriod, loading: loadingMyPeriod }] =
    useFindMyRankingByPeriodLazyQuery();
  const myPeriodRanking = dataMyPeriod?.findMyRankingByPeriod ?? null;

  useEffect(() => {
    if (activeTab !== 'mes') return;
    const { startDate, endDate } = getMonthPeriod(selectedMonth);
    fetchPeriod({ variables: { startDate, endDate, limit: 20 } });
    if (profileComplete) {
      fetchMyPeriod({ variables: { startDate, endDate } });
    }
  }, [activeTab, selectedMonth, profileComplete, fetchPeriod, fetchMyPeriod]);

  const isLoadingMes = loadingPeriod || loadingMyPeriod;
  const myPositionInPeriod = myPeriodRanking?.position;
  const myScoreInPeriod = myPeriodRanking?.score;
  const isInTop20Period = listUsersPeriod?.some(u => u.key === dataProfile?.key);

  return (
    <Index>
      <Posts />

      <S.BackgroundSection>
        <S.TabsContainer>
          <S.TabButton active={activeTab === 'mes'} onClick={() => setActiveTab('mes')}>
            Ranking no Mês
          </S.TabButton>
          <S.TabButton active={activeTab === 'geral'} onClick={() => setActiveTab('geral')}>
            Ranking Geral
          </S.TabButton>
        </S.TabsContainer>

        {activeTab === 'geral' && (
          <>
            {loadingGeral ? (
              <Loading />
            ) : !profileComplete ? (
              <div className="notRanking">
                <UndrawOrganizeResume />
                <span>
                  Complete seu perfil para visualizar a sua posição no ranking geral
                </span>
                <Link to="/dashboard/perfil">Completar perfil</Link>
              </div>
            ) : (
              <>
                <h1>Top 10 - Ranking Geral</h1>
                <Table borderless>
                  <tbody>
                    {(listUsersRanking ?? []).slice(0, 10).map(userRanking => (
                      <tr key={userRanking.key}>
                        <td>
                          <img
                            src={userRanking.avatarUrl ? userRanking.avatarUrl : avatar}
                            alt={userRanking.nickname}
                          />
                          <span className="names">
                            {userRanking.nickname}{' '}
                            <font>
                              {isProfileComplete(userRanking)
                                ? `(${userRanking.fullname})`
                                : ''}
                            </font>
                          </span>
                        </td>
                        <td>
                          <span className="orange">
                            {userRanking.ranking.position}°
                          </span>
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Total de pontos</Tooltip>}
                          >
                            <span className="grey">{userRanking.ranking.score}</span>
                          </OverlayTrigger>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {listUsersRanking?.length > 0 && dataProfile?.ranking?.position > 10 && (
                  <>
                    <S.EllipsisContainer>
                      <div className="ellipsis-dot"></div>
                      <div className="ellipsis-dot"></div>
                      <div className="ellipsis-dot"></div>
                    </S.EllipsisContainer>

                    <Table borderless>
                      <tbody>
                        <tr>
                          <td>
                            <img
                              src={dataProfile.avatar ? dataProfile.avatar : avatar}
                              alt={dataProfile.nickname}
                            />
                            <span className="names">
                              {dataProfile.nickname}{' '}
                              <font>
                                {dataPerson.fullname ? `(${dataPerson.fullname})` : ''}
                              </font>
                            </span>
                          </td>
                          <td>
                            <span className="orange">
                              {dataProfile?.ranking?.position}°
                            </span>
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>Total de pontos</Tooltip>}
                            >
                              <span className="grey">{dataProfile?.ranking?.score}</span>
                            </OverlayTrigger>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </>
                )}
              </>
            )}
          </>
        )}

        {activeTab === 'mes' && (
          <>
            {!profileComplete ? (
              <div className="notRanking">
                <UndrawOrganizeResume />
                <span>
                  Complete seu perfil para visualizar o ranking do mês
                </span>
                <Link to="/dashboard/perfil">Completar perfil</Link>
              </div>
            ) : (
              <>
                <S.MonthSelectWrapper>
                  <select
                    value={selectedMonth}
                    onChange={e => setSelectedMonth(e.target.value)}
                  >
                    {monthOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </S.MonthSelectWrapper>

                {isLoadingMes ? (
                  <Loading />
                ) : (
                  <>
                    <h1>
                      Top 20 - {monthOptions.find(o => o.value === selectedMonth)?.label}
                    </h1>

                    {listUsersPeriod?.length === 0 ? (
                      <S.NotScoredMessage>
                        Nenhum usuário pontuou neste período ainda.
                      </S.NotScoredMessage>
                    ) : (
                      <Table borderless>
                        <tbody>
                          {(listUsersPeriod ?? []).map(userRanking => (
                            <tr key={userRanking.key}>
                              <td>
                                <img
                                  src={userRanking.avatarUrl ? userRanking.avatarUrl : avatar}
                                  alt={userRanking.nickname}
                                />
                                <span className="names">
                                  {userRanking.nickname}{' '}
                                  <font>
                                    {isProfileComplete(userRanking)
                                      ? `(${userRanking.fullname})`
                                      : ''}
                                  </font>
                                </span>
                              </td>
                              <td>
                                <span className="orange">
                                  {userRanking.ranking.position}°
                                </span>
                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip>Pontos no período</Tooltip>}
                                >
                                  <span className="grey">{userRanking.ranking.score}</span>
                                </OverlayTrigger>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}

                    {profileComplete &&
                      myPositionInPeriod &&
                      !isInTop20Period && (
                        <>
                          <S.EllipsisContainer>
                            <div className="ellipsis-dot"></div>
                            <div className="ellipsis-dot"></div>
                            <div className="ellipsis-dot"></div>
                          </S.EllipsisContainer>

                          <Table borderless>
                            <tbody>
                              <tr>
                                <td>
                                  <img
                                    src={dataProfile?.avatar ? dataProfile.avatar : avatar}
                                    alt={dataProfile?.nickname}
                                  />
                                  <span className="names">
                                    {dataProfile?.nickname}{' '}
                                    <font>
                                      {dataPerson?.fullname
                                        ? `(${dataPerson.fullname})`
                                        : ''}
                                    </font>
                                  </span>
                                </td>
                                <td>
                                  <span className="orange">{myPositionInPeriod}°</span>
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip>Pontos no período</Tooltip>}
                                  >
                                    <span className="grey">{myScoreInPeriod ?? 0}</span>
                                  </OverlayTrigger>
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                        </>
                      )}

                    {profileComplete && !myPositionInPeriod && (
                      <S.NotScoredMessage>
                        Você ainda não pontuou neste período.
                      </S.NotScoredMessage>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </S.BackgroundSection>
    </Index>
  );
}
